import { isFetchError } from "fetcher"
import * as api from "../../api"
import { MAX_ATTACHMENT_BYTES } from "../../constants"
import type { Jid } from "../../xmpp/jid"
import type { ChatSession } from "../session"
import { call } from "./call"

/**
 * Upload an image for `peer`'s conversation. Rethrows what is not an outcome,
 * like the group calls: the composer shows it.
 */
export function uploadAttachment(
  session: ChatSession,
  peer: Jid,
  file: File,
  onProgress: (fraction: number) => void
): Promise<api.UploadOutcome> {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return Promise.resolve({ status: "rejected", reason: "size" })
  }

  return call(session, (token) =>
    api.uploadAttachment(token, peer, file, onProgress)
  )
}

/** View URLs by Attachment id, with when to stop trusting each. */
export type AttachmentUrls = Map<string, { url: string; staleAt: number }>

// A view URL works for 15 minutes (`getAttachmentUrl`). Reusing one for 10
// means a cached link never starts loading an image just as it expires.
const URL_REUSE_MS = 10 * 60 * 1000

/**
 * The URL to show an Attachment, or null when it is gone or not ours to see.
 * `fresh` skips the cache: the image failed to load from the cached URL.
 */
export async function attachmentUrl(
  session: ChatSession,
  urls: AttachmentUrls,
  id: string,
  { fresh = false } = {}
): Promise<string | null> {
  const cached = urls.get(id)
  if (!fresh && cached && Date.now() < cached.staleAt) return cached.url

  try {
    const url = await call(session, (token) => api.getAttachmentUrl(token, id))
    urls.set(id, { url, staleAt: Date.now() + URL_REUSE_MS })
    return url
  } catch (error) {
    if (!isFetchError(error) || error.status !== 404) throw error
    // Gone for good: never serve its last URL again.
    urls.delete(id)
    return null
  }
}
